import type { BrandSpecials } from '@iheartjane/data-access';
import { FLAGS } from '@iheartjane/feature-flags';
import type { CardCarouselProps } from '@iheartjane/menu';
import { CardCarousel, MOBILE_PRODUCT_CARD_WIDTH } from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';
import { useMemo } from 'react';

import { ProductCard } from '@jane/bloom/components/productCard/productCard';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';

import styles from './dmPDPRow.module.css';

type DMProductDetailsRowProps = {
  brandSpecials: BrandSpecials;
  cart?: Nullable<Cart>;
  customLanguage: CustomLanguage;
  mode?: CardCarouselProps['mode'];
  onProductSelected?: (product: ProductSearchResultViewModel) => void;
  recommendedPDPRow: string | undefined;
  shouldShowGold: boolean;
  specials: StoreSpecials;
};

export const DMProductDetailsRow = ({
  brandSpecials,
  cart,
  customLanguage,
  mode = 'ideal-width',
  onProductSelected,
  recommendedPDPRow,
  shouldShowGold,
  specials,
}: DMProductDetailsRowProps) => {
  const { featureFlags } = useRootLoaderData();
  const pdpRow = useMemoizedRowInstance(recommendedPDPRow);

  const areProductsAvailable = (pdpRow?.products.length || -1) > 0;

  const onCardSelect = async (product: ProductSearchResultViewModel) => {
    // Handle navigation to the PDP and Mixpanel tracking
    onProductSelected?.(product);
  };

  const isRowSponsored = useMemo(() => {
    if (!pdpRow || !pdpRow.products.length) {
      return false;
    }

    return (
      pdpRow.products.filter((product) => product.isSponsored).length ===
      pdpRow.products.length
    );
  }, [pdpRow]);

  if (!pdpRow || !areProductsAvailable) {
    return null;
  }

  return (
    <CardCarousel
      cardWidth={240}
      cardWidthMobile={MOBILE_PRODUCT_CARD_WIDTH}
      className={styles['dmProductDetailsRowContainer']}
      data-testid="sponsored-product-details-row"
      mobileScrollPadding={16}
      mode={mode}
      name={pdpRow.title || 'You may also like'}
      surtitle={isRowSponsored ? 'Sponsored' : undefined}
    >
      {pdpRow.products.map((product, ind) => {
        const builtProduct = buildSingleProductSearchResultViewModel({
          bundleTag: featureFlags[FLAGS.c1BundleTag],
          cart,
          customLanguage,
          precalculatedShouldShowGold: shouldShowGold,
          product: product.attributes,
          specials,
        });

        return (
          <CardCarousel.Card key={product.objectId}>
            <ProductCard
              brandSpecials={brandSpecials}
              product={builtProduct}
              selected={() => {
                onCardSelect(builtProduct);
                product.click();
              }}
              smartProductInstance={product}
              preloaded={ind <= 5}
              isSponsored={isRowSponsored ? false : product.isSponsored}
            />
          </CardCarousel.Card>
        );
      })}
    </CardCarousel>
  );
};
